import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Clock, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Upload, Trash2, Eye, BarChart2 } from 'lucide-react';
import { db, ensureDbOpen } from '../db/database';
import { createOrder, getDeviceUUID } from '../services/orderService';
import { useAuth } from '../context/AuthContext';

const OrdersList = () => {
  const [orders, setOrders] = useState([]);
  const [reversedOrders, setReversedOrders] = useState([]);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [errorViewId, setErrorViewId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [reverseTargetOrder, setReverseTargetOrder] = useState(null);
  const [reverseReason, setReverseReason] = useState('');
  const [reverseError, setReverseError] = useState('');
  const navigate = useNavigate();
  const { user, node } = useAuth();

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      await ensureDbOpen();
      const ordersData = await db.orders.orderBy('createdAt').reverse().toArray();
      
      // Load items for each order
      const ordersWithItems = await Promise.all(
        ordersData.map(async (order) => {
          await ensureDbOpen();
          const items = await db.orderItems.where('orderId').equals(order.id).toArray();
          
          // Get product details for each item
          const itemsWithDetails = await Promise.all(
            items.map(async (item) => {
              await ensureDbOpen();
              const product = await db.products.get(item.productId);
              return {
                ...item,
                productName: product?.name || 'Producto desconocido'
              };
            })
          );
          
          return {
            ...order,
            items: itemsWithDetails
          };
        })
      );
      
      setOrders(ordersWithItems);

      // Load reversed orders for separate listing
      const reversed = await db.ordersReversed?.orderBy('reversedAt').reverse().toArray();
      if (reversed) {
        setReversedOrders(reversed);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReverseConfirm = async () => {
    const order = reverseTargetOrder;
    if (!order || order.uploadStatus === 'done' || order.status !== 'pending') return;

    const trimmedReason = (reverseReason || '').trim();
    if (!trimmedReason) {
      setReverseError('Debes indicar una observación para reversar la orden.');
      return;
    }

    try {
      setIsDeleting(true);
      await ensureDbOpen();

      // Cargar ítems asociados a la orden
      const items = await db.orderItems.where('orderId').equals(order.id).toArray();

      // Guardar en ordersReversed (solo metadatos básicos + razón)
      await db.ordersReversed.add({
        originalOrderId: order.id,
        uuid: order.uuid,
        stationId: order.stationId,
        nodeId: order.nodeId,
        userId: order.userId,
        username: order.username,
        createdAt: order.createdAt,
        total: order.total,
        reason: trimmedReason,
        reversedAt: new Date().toISOString(),
        // Opcional: guardar snapshot de ítems para auditoría
        itemsSnapshot: items.map((i) => ({
          productId: i.productId,
          quantity: i.quantity,
          price: i.price,
        })),
      });

      // Eliminar orden original e ítems
      await db.transaction('rw', db.orderItems, db.orders, async () => {
        await db.orderItems.where('orderId').equals(order.id).delete();
        await db.orders.delete(order.id);
      });

      setConfirmDeleteId(null);
      setReverseTargetOrder(null);
      setReverseReason('');
      setReverseError('');
      await loadOrders();
    } catch (e) {
      console.error('Error reversing order', order.id, e);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      setSyncMessage('Iniciando subida...');
      await ensureDbOpen();
      // Include orders without uploadStatus (older records) as pending
      const pending = await db.orders.filter(o => o.uploadStatus !== 'done').toArray();
      console.log('Pending orders to sync:', pending.length);
      if (!pending.length) {
        setSyncMessage('No hay órdenes pendientes por subir.');
        return;
      }
      let successCount = 0;
      let failCount = 0;

      for (const order of pending) {
        try {
          setSyncMessage(`Subiendo orden ${order.uuid?.slice(0,8)}...`);
          await ensureDbOpen();
          const items = await db.orderItems.where('orderId').equals(order.id).toArray();
          const nodeIdToSend = order.nodeId || node?.id;
          if (!nodeIdToSend) {
            console.warn('Order without nodeId and no selected node. Marking as error.', order);
            await db.orders.update(order.id, { uploadStatus: 'error' });
            failCount++;
            continue;
          }
          
          // Build payload according to backend spec
          const payload = {
            uuid: order.uuid,
            node_id: nodeIdToSend,
            total: order.total,
            uuid_local: getDeviceUUID(),
            operator_name: order.username || user?.name || user?.username || 'Operador',
            datetime_device: order.createdAt,
            details: items.map(i => ({
              product_id: i.productId,
              quantity: i.quantity,
              unit_price: i.price
            }))
          };
          
          console.log('Sync payload:', payload);
          const resp = await createOrder(payload);
          const syncedAt = new Date().toISOString();
          await ensureDbOpen();
          await db.orders.update(order.id, {
            status: 'synced',
            uploadStatus: 'done',
            syncedAt,
            remoteId: resp?.id || resp?.orderId || null,
          });
          for (const it of items) {
            await db.orderItems.update(it.id, { uploadStatus: 'done' });
          }
          successCount++;
        } catch (err) {
          console.error('Sync error for order', order.id, err);
          // Try to extract useful info from error
          let lastErrorMessage = err?.message || 'Sync error';
          let lastErrorPayload = null;
          try {
            // Many fetch wrappers attach response JSON/data
            lastErrorPayload = err?.response || err?.data || err?.body || null;
          } catch {}
          await ensureDbOpen();
          await db.orders.update(order.id, { 
            uploadStatus: 'error',
            status: 'error',
            lastErrorMessage,
            lastErrorPayload
          });
          failCount++;
        }
      }
      setSyncMessage(`Subida finalizada: ${successCount} ok, ${failCount} error.`);
      await loadOrders();
    } finally {
      setIsSyncing(false);
      setTimeout(() => setSyncMessage(''), 3000);
    }
  };

  const toggleExpand = (orderId) => {
    setExpandedOrder(expandedOrder === orderId ? null : orderId);
  };

  const requestDelete = (orderId) => {
    setConfirmDeleteId(orderId);
  };

  const cancelDelete = () => {
    setConfirmDeleteId(null);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      setIsDeleting(true);
      await ensureDbOpen();
      await db.transaction('rw', db.orderItems, db.orders, async () => {
        await db.orderItems.where('orderId').equals(confirmDeleteId).delete();
        await db.orders.delete(confirmDeleteId);
      });
      setConfirmDeleteId(null);
      await loadOrders();
    } catch (e) {
      console.error('Error deleting order', confirmDeleteId, e);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusIcon = (status, uploadStatus) => {
    const st = uploadStatus === 'done' ? 'synced' : (uploadStatus === 'error' ? 'error' : status);
    switch (st) {
      case 'pending':
        return <Clock size={20} className="text-yellow-500" />;
      case 'synced':
        return <CheckCircle size={20} className="text-green-500" />;
      case 'error':
        return <AlertCircle size={20} className="text-red-500" />;
      default:
        return <Clock size={20} className="text-gray-500" />;
    }
  };

  const getStatusText = (status, uploadStatus) => {
    if (uploadStatus === 'done') return 'Subida';
    if (uploadStatus === 'error') return 'Error';
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'synced':
        return 'Subida';
      case 'error':
        return 'Error';
      default:
        return status;
    }
  };

  const getStatusColor = (status, uploadStatus) => {
    const st = uploadStatus === 'done' ? 'synced' : (uploadStatus === 'error' ? 'error' : status);
    switch (st) {
      case 'pending':
        return 'bg-yellow-500/10 border-yellow-500 text-yellow-300';
      case 'synced':
        return 'bg-green-500/10 border-green-500 text-green-300';
      case 'error':
        return 'bg-red-500/10 border-red-500 text-red-300';
      default:
        return 'bg-gray-500/10 border-gray-500 text-gray-300';
    }
  };

  const getAnalytics = () => {
    const map = new Map();
    let grandTotal = 0;
    let totalQuantity = 0;

    orders.forEach((order) => {
      (order.items || []).forEach((item) => {
        const key = item.productId ?? item.productName ?? String(item.id ?? 'unknown');
        const existing = map.get(key) || {
          productId: item.productId,
          productName: item.productName || 'Producto desconocido',
          quantity: 0,
          total: 0,
        };
        const qty = item.quantity || 0;
        existing.quantity += qty;
        const lineTotal = (item.price || 0) * qty;
        existing.total += lineTotal;
        grandTotal += lineTotal;
        totalQuantity += qty;
        map.set(key, existing);
      });
    });

    const items = Array.from(map.values()).sort((a, b) => b.total - a.total);
    return { items, grandTotal, totalQuantity };
  };

  const analytics = showAnalytics ? getAnalytics() : { items: [], grandTotal: 0, totalQuantity: 0 };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Cargando órdenes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pb-32">
      <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-4 sticky top-0 z-10 shadow-lg">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/products')} className="p-2 bg-white/20 rounded-lg">
            <ArrowLeft size={24} className="text-white" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-white">Órdenes Locales</h1>
            <p className="text-sm text-white/80">
              {orders.length} {orders.length === 1 ? 'orden' : 'órdenes'} registradas
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAnalytics((prev) => !prev)}
              className="p-2 bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors flex items-center gap-1 text-xs"
            >
              <BarChart2 size={16} /> {showAnalytics ? 'Ocultar análisis' : 'Ver análisis'}
            </button>
            <div className="p-2 bg-white/20 rounded-lg">
              <ShoppingCart size={22} className="text-white" />
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm text-gray-400">{orders.length} {orders.length === 1 ? 'orden' : 'órdenes'}</div>
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className={`px-3 py-2 rounded-lg text-white flex items-center gap-2 ${isSyncing ? 'bg-gray-600' : 'bg-primary-600 hover:bg-primary-700'} transition-colors`}
          >
            <Upload size={18} /> {isSyncing ? 'Subiendo...' : 'Subir'}
          </button>
        </div>
        {syncMessage && (
          <div className="text-xs text-gray-400 mb-1">{syncMessage}</div>
        )}
        {showAnalytics && (
          <div className="card p-4 mb-3">
            <div className="mb-2">
              <h2 className="text-sm font-semibold text-white">Análisis de ventas</h2>
              <p className="text-xs text-gray-400">Producto - Cantidad vendida (órdenes locales)</p>
            </div>
            <div className="border border-gray-800 rounded-lg overflow-hidden">
              <div className="grid grid-cols-2 bg-gray-800/80 px-3 py-2 text-xs font-semibold text-gray-200">
                <div>Producto</div>
                <div className="text-right">Cantidad vendida</div>
              </div>
              <div className="max-h-64 overflow-auto divide-y divide-gray-800">
                {analytics.items.length === 0 ? (
                  <div className="p-4 text-center text-sm text-gray-400">
                    No hay ventas registradas todavía.
                  </div>
                ) : (
                  analytics.items.map((p) => (
                    <div
                      key={p.productId || p.productName}
                      className="grid grid-cols-2 px-3 py-2 text-sm items-center"
                    >
                      <div className="pr-2 text-white truncate" title={p.productName}>
                        {p.productName}
                      </div>
                      <div className="text-right text-gray-200">
                        {p.quantity}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-gray-300 border-t border-gray-800 pt-3">
              <div>
                <p className="uppercase tracking-wide text-[10px] text-gray-400">Total unidades vendidas</p>
                <p className="font-semibold text-white text-sm">{analytics.totalQuantity}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="h-8 w-px bg-gray-700" />
                <div className="text-right">
                  <p className="uppercase tracking-wide text-[10px] text-gray-400">Monto total vendido</p>
                  <p className="font-semibold text-lime-300 text-xl drop-shadow-[0_0_6px_rgba(190,242,100,0.6)]">
                    ${analytics.grandTotal.toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {orders.length === 0 ? (
          <div className="card p-8 text-center">
            <ShoppingCart size={48} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 text-lg">No hay órdenes registradas</p>
            <p className="text-gray-500 text-sm mt-2">Las órdenes aparecerán aquí cuando realices ventas</p>
          </div>
        ) : (
          orders.map((order) => (
            <div key={order.id} className="card overflow-hidden">
              <div 
                onClick={() => toggleExpand(order.id)}
                className="p-4 cursor-pointer hover:bg-gray-800/50 transition"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white font-bold text-lg">#{order.uuid?.slice(0, 8)}</span>
                      {getStatusIcon(order.status, order.uploadStatus)}
                    </div>
                    {errorViewId === order.id && (order.uploadStatus === 'error' || order.status === 'error') && (
                      <div className="p-4 border-t border-yellow-800 bg-yellow-900/20">
                        <p className="text-yellow-300 text-sm font-semibold mb-2">Detalle del error de subida</p>
                        {order.lastErrorMessage && (
                          <p className="text-yellow-200 text-sm mb-2">{order.lastErrorMessage}</p>
                        )}
                        <pre className="text-xs text-yellow-100 whitespace-pre-wrap overflow-auto max-h-64">
{typeof order.lastErrorPayload === 'string' ? order.lastErrorPayload : JSON.stringify(order.lastErrorPayload, null, 2)}
                        </pre>
                      </div>
                    )}
                    <p className="text-gray-400 text-sm">
                      {new Date(order.createdAt).toLocaleString('es-ES', {
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-primary-400 font-bold text-xl">${order.total.toFixed(2)}</p>
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs border mt-1 ${getStatusColor(order.status, order.uploadStatus)}`}>
                      {getStatusText(order.status, order.uploadStatus)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <div className="text-gray-400">
                    <span className="font-semibold text-gray-300">Operador:</span> {order.username}
                  </div>
                  <div className="flex items-center gap-2 text-gray-400">
                    <span>{order.items?.length || 0} productos</span>
                    {(order.uploadStatus === 'error' || order.status === 'error') && (
                      <>
                        <button
                          onClick={(e) => { e.stopPropagation(); setErrorViewId(order.id); }}
                          className="p-2 rounded bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400"
                          title="Ver error de subida"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); requestDelete(order.id); }}
                          className="p-2 rounded bg-red-600/20 hover:bg-red-600/30 text-red-400"
                          title="Eliminar orden con error"
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                    {order.status === 'pending' && order.uploadStatus !== 'done' && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setReverseTargetOrder(order);
                          setReverseReason('');
                          setReverseError('');
                        }}
                        className="p-2 rounded bg-yellow-600/20 hover:bg-yellow-600/30 text-yellow-400 text-xs"
                        title="Reversar orden pendiente"
                      >
                        Reversar
                      </button>
                    )}
                    {expandedOrder === order.id ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )}
                  </div>
                </div>
              </div>

              {expandedOrder === order.id && (
                <div className="border-t border-gray-800 bg-gray-800/30 p-4">
                  <h4 className="text-gray-300 font-semibold mb-3 text-sm">Detalle de productos:</h4>
                  <div className="space-y-2">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <div className="flex-1">
                          <p className="text-white">{item.productName}</p>
                          <p className="text-gray-400 text-xs">Cantidad: {item.quantity}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-300">${item.price.toFixed(2)} c/u</p>
                          <p className="text-primary-400 font-semibold">
                            ${(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-700 mt-3 pt-3 flex justify-between items-center">
                    <span className="text-white font-semibold">Total</span>
                    <span className="text-primary-400 font-bold text-lg">${order.total.toFixed(2)}</span>
                  </div>
                </div>
              )}
              {confirmDeleteId === order.id && (
                <div className="p-4 border-t border-red-800 bg-red-900/20">
                  <p className="text-red-300 text-sm mb-3">
                    Advertencia: Esta acción eliminará permanentemente la orden y sus detalles. Esta operación no se puede deshacer.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); confirmDelete(); }}
                      disabled={isDeleting}
                      className={`px-3 py-2 rounded-lg text-white ${isDeleting ? 'bg-red-700' : 'bg-red-600 hover:bg-red-700'} transition-colors`}
                    >
                      {isDeleting ? 'Eliminando...' : 'Eliminar definitivamente'}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); cancelDelete(); }}
                      disabled={isDeleting}
                      className="px-3 py-2 rounded-lg text-white bg-gray-700 hover:bg-gray-600 transition-colors"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
        <div className="mt-4 flex flex-col gap-3">
          <button
            onClick={() => navigate('/orders-reversed')}
            className="btn-secondary w-full flex items-center justify-center gap-2 text-xs"
          >
            <ArrowLeft size={14} />
            <span>Ver órdenes reversadas</span>
          </button>
        </div>
      </div>
      {reverseTargetOrder && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-30">
          <div className="card w-full max-w-md p-5">
            <h2 className="text-lg font-semibold text-white mb-2">Reversar orden</h2>
            <p className="text-xs text-gray-400 mb-3">
              Orden #{reverseTargetOrder.uuid?.slice(0, 8)} • Total ${reverseTargetOrder.total?.toFixed(2)}
            </p>
            <p className="text-xs text-gray-300 mb-2">Escribe la razón del reverso:</p>
            <textarea
              value={reverseReason}
              onChange={(e) => setReverseReason(e.target.value)}
              rows={3}
              className="input-field w-full text-sm"
              placeholder="Ej: Error en el monto, cliente canceló la compra, etc."
            />
            {reverseError && (
              <p className="text-xs text-red-400 mt-1">{reverseError}</p>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setReverseTargetOrder(null);
                  setReverseReason('');
                  setReverseError('');
                }}
                disabled={isDeleting}
                className="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-xs"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleReverseConfirm}
                disabled={isDeleting}
                className={`px-3 py-2 rounded-lg text-white text-xs ${isDeleting ? 'bg-yellow-700' : 'bg-yellow-600 hover:bg-yellow-500'}`}
              >
                {isDeleting ? 'Reversando...' : 'Confirmar reverso'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersList;
